import { forwardRef } from 'react';
import type { Ref, MouseEvent, KeyboardEvent, ReactNode, PropsWithoutRef } from 'react';
import styled from 'styled-components';

import type { BaseProps, ForwardRefForwardPropsComponent } from '../../types';

export interface BareRoleButtonProps extends BaseProps {
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  ref?: Ref<HTMLDivElement>;
}

export const StyledBareRoleButton = styled.div`
  cursor: pointer;
`;

const BareRoleButton: ForwardRefForwardPropsComponent<BareRoleButtonProps> = forwardRef(
  function BareRoleButton(
    { children, onClick, onKeyDown, ...restProps }: PropsWithoutRef<BareRoleButtonProps>,
    ref: BareRoleButtonProps['ref']
  ) {
    return (
      <StyledBareRoleButton
        {...restProps}
        tabIndex={0}
        role='button'
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if ((e.key === ' ' || e.key === 'Enter') && e.target === e.currentTarget) {
            (e.currentTarget as HTMLDivElement).click();
          }

          return onKeyDown?.(e);
        }}
        onClick={(e: MouseEvent<HTMLDivElement>) => {
          if (
            e.target instanceof Element &&
            // Ignore clicks on other interactive elements within this element.
            e.target.closest("button, a, [role='button'], [role='link']") !== e.currentTarget
          )
            return;

          return onClick?.(e);
        }}
        ref={ref}
      >
        {children}
      </StyledBareRoleButton>
    );
  }
);

export default BareRoleButton;
